import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Label,
  Text,
  tokens,
  mergeClasses,
  makeStyles,
} from '@fluentui/react-components';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { UrlRulesEditor } from './side-panel/url-rules-editor';
import { UrlRulesPreview } from './url-rules-preview';
import { extractTargetsFromViewModel } from './helpers/utils';

const useUrlRulesStyles = makeStyles({
  title: {
    marginBottom: tokens.spacingVerticalXS,
  },
  editButton: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
});

export const UrlRulesImpl = (props) => {
  const {
    i18n,
    urlRules,
    onChange,
    enableSpecialCharactersValidation,
  } = props;
  const styles = useUrlRulesStyles();

  const { urlTargets } = useMemo(() => extractTargetsFromViewModel(urlRules), [urlRules]);

  const [isUrlRulesEditorOn, setIsUrlRulesEditorOn] = useState(false);
  const onOpenUrlRulesEditor = useCallback(() => setIsUrlRulesEditorOn(true), []);
  const onCloseUrlRulesEditor = useCallback(() => setIsUrlRulesEditorOn(false), []);

  const renderEditor = useCallback(() => {
    if (isUrlRulesEditorOn) {
      return (
        <UrlRulesEditor
          i18n={i18n}
          urlRules={urlRules}
          onChange={onChange}
          isOpen={isUrlRulesEditorOn}
          onClose={onCloseUrlRulesEditor}
          enableSpecialCharactersValidation={enableSpecialCharactersValidation}
        />
      );
    }

    return null;
  }, [isUrlRulesEditorOn, i18n, urlRules, onChange, onCloseUrlRulesEditor, enableSpecialCharactersValidation]);

  const renderCreateRulesButton = useCallback(() => (
    <div>
      <UrlRulesPreview
        urlRules={urlRules}
        i18n={i18n}
      />
      <Button
        className={mergeClasses(styles.editButton, 'url-rules-edit-button')}
        appearance="secondary"
        size="small"
        onClick={onOpenUrlRulesEditor}
      >
        {(urlTargets.length > 0)
          ? i18n.getString(_TL_('Manage'))
          : i18n.getString(_TL_('Create'))}
      </Button>
    </div>
  ), [i18n, urlTargets.length, urlRules, onOpenUrlRulesEditor]);

  return (
    <div className="url-rules-container">
      <Label className={styles.title} weight="semibold">
          {i18n.getString(_TL_('URL rules and custom labels'))}
          <HelpPopupInline topic="pop_BA_PMAX_RulesLabels" />
      </Label>
      { urlRules.length === 0 && <Text size={300} weight="regular">{i18n.getString(_TL_('Your campaign will target all webpages associated with your Final URL by default.'))}</Text> }
      {renderCreateRulesButton()}
      {renderEditor()}
    </div>
  );
};

UrlRulesImpl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  urlRules: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number,
    TargetCondition1: PropTypes.string,
    TargetConditionOperator1: PropTypes.string,
    TargetValue1: PropTypes.string,
    TargetCondition2: PropTypes.string,
    TargetConditionOperator2: PropTypes.string,
    TargetValue2: PropTypes.string,
    TargetCondition3: PropTypes.string,
    TargetConditionOperator3: PropTypes.string,
    TargetValue3: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  enableSpecialCharactersValidation: PropTypes.bool,
};

UrlRulesImpl.defaultProps = {
  urlRules: [],
  enableSpecialCharactersValidation: false,
};
